import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  Autocomplete,
} from "@mui/material";

export default function DataTable({ rows, darkMode }) {
  const [filters, setFilters] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const columns = useMemo(
    () => (rows.length ? Object.keys(rows[0]) : []),
    [rows]
  );

  const uniqueValues = useMemo(() => {
    const uniques = {};
    columns.forEach((col) => {
      uniques[col] = [...new Set(rows.map((r) => r[col]).filter(Boolean))];
    });
    return uniques;
  }, [rows, columns]);

  const handleAddFilter = (col, val) => {
    setFilters((prev) => {
      if (!val) return prev.filter((f) => f.column !== col);
      const exists = prev.find((f) => f.column === col);
      if (exists) {
        return prev.map((f) =>
          f.column === col ? { column: col, value: val } : f
        );
      }
      return [...prev, { column: col, value: val }];
    });
    setPage(0);
  };

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    return rows.filter((row) =>
      filters.every(
        (f) => !f.value || String(row[f.column]) === String(f.value)
      )
    );
  }, [rows, filters]);

  return (
    <Paper
      elevation={6}
      sx={{
        width: "100%",
        maxHeight: 600,
        overflow: "auto",
        borderRadius: 3,
        p: 2,
        bgcolor: darkMode ? "#1e293b" : "#ffffff",
        "&::-webkit-scrollbar": {
          width: "6px",
          height: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
          bgcolor: darkMode ? "#475569" : "#90a4ae",
          borderRadius: 2,
        },
        "&::-webkit-scrollbar-track": {
          bgcolor: darkMode ? "#1e293b" : "#f1f5f9",
        },
      }}
    >
      <TableContainer>
        <Table stickyHeader size="medium">
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                const currentFilter = filters.find((f) => f.column === col);
                return (
                  <TableCell
                    key={col}
                    sx={{
                      borderBottom: "1px solid #90caf9",
                      px: 1,
                      py: 1,
                      minWidth: 140,
                      bgcolor: darkMode ? "#111827" : "#f1f5f9",
                      color: darkMode ? "#e0e7ff" : "#1e3a8a",
                      fontWeight: 700,
                    }}
                  >
                    <Autocomplete
                      options={uniqueValues[col] || []}
                      value={currentFilter?.value || null}
                      onChange={(_, newValue) => handleAddFilter(col, newValue)}
                      size="small"
                      PopperProps={{
                        sx: {
                          "& .MuiPaper-root": {
                            maxHeight: 200,
                            "&::-webkit-scrollbar": {
                              width: "6px",
                              height: "6px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              bgcolor: darkMode ? "#475569" : "#90a4ae",
                              // borderRadius: 2,
                            },
                            "&::-webkit-scrollbar-track": {
                              bgcolor: darkMode ? "#1e293b" : "#f1f5f9",
                            },
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={col}
                          variant="outlined"
                          size="small"
                          sx={{
                            bgcolor: currentFilter
                              ? darkMode
                                ? "#334155"
                                : "#d0e3fc"
                              : darkMode
                              ? "#1e293b"
                              : "#e9f0fb",
                            // borderRadius: 2,
                            "& .MuiInputLabel-root": {
                              color: currentFilter
                                ? darkMode
                                  ? "#ffffff"
                                  : "#030303"
                                : darkMode
                                ? "#94a3b8"
                                : "#1976d2",
                              fontWeight: currentFilter ? 700 : 600,
                            },
                            "& .MuiOutlinedInput-root": {
                              color: darkMode ? "#e2e8f0" : "#0d47a1",
                              "& fieldset": {
                                borderColor: currentFilter
                                  ? darkMode
                                    ? "#475569"
                                    : "#1976d2"
                                  : darkMode
                                  ? "#334155"
                                  : "#90a4ae",
                              },
                              "&:hover fieldset": {
                                borderColor: currentFilter
                                  ? darkMode
                                    ? "#94a3b8"
                                    : "#1565c0"
                                  : darkMode
                                  ? "#64748b"
                                  : "#5c6bc0",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: currentFilter
                                  ? darkMode
                                    ? "#0ea5e9"
                                    : "#0d47a1"
                                  : darkMode
                                  ? "#60a5fa"
                                  : "#3f51b5",
                              },
                            },
                            input: {
                              color: darkMode ? "#e2e8f0" : "#0d47a1",
                            },
                          }}
                        />
                      )}
                      clearOnEscape
                      disableClearable={false}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredRows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    "&:nth-of-type(odd)": {
                      bgcolor: darkMode ? "#1f2a38" : "#f9fafb",
                    },
                    "&:hover": {
                      bgcolor: darkMode ? "#2c3a4b" : "#e2e8f0",
                      transition: "background-color 0.3s ease",
                    },
                    cursor: "default",
                  }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col}
                      sx={{
                        fontSize: "0.95rem",
                        color: darkMode ? "#e2e8f0" : "#374151",
                        whiteSpace: "nowrap",
                        px: 2,
                        py: 1,
                      }}
                    >
                      {row[col]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2} display="flex" justifyContent="flex-end">
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            mt: 2,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.8rem",
              },
            "& .MuiTablePagination-toolbar": {
              color: darkMode ? "#e2e8f0" : "#1e293b",
            },
          }}
        />
      </Box>
    </Paper>
  );
}
